'use strict';

/**
 * SuperApiIntegrationSettingsController
 *
 * Super Admin → Settings → API Integration Settings (WEB-4610). Manages the outbound partner
 * integrations stored in `api_integration_settings` (e.g. PropNex BALLOTING_PUSH /
 * EOI_STATUS_PUSH): endpoint URL, credentials, linked entity, developer scope and the
 * runtime enable switch.
 *
 * API (all under sec/super/api-integration-settings):
 *   GET    /           list (rows carry has_secret_key / has_api_key / has_token and
 *                      developer_name / partner_entity_name — never the secrets)
 *   GET    /options    partners, categories, auth types (+ the credential fields each needs),
 *                      entity types, and id/name lists for the entity + developer pickers
 *   POST   /           create
 *   PUT    /{id}       PARTIAL update — an omitted field (secrets included) keeps the stored
 *                      value; "" on a nullable field stores NULL
 *   DELETE /{id}
 *
 * Permissions (Super role bypasses all, via SSUserService.hasPermission):
 *   SS_API_INTEGRATION_SETTING_VW     page + list
 *   SS_API_INTEGRATION_SETTING_EDIT   create, edit, enable/disable
 *   SS_API_INTEGRATION_SETTING_DELETE delete
 *
 * Secrets (secret_key, api_key, token) are write-only:
 *   - create: plain password inputs;
 *   - edit: "Secret is set" + Replace; only a replaced, non-empty secret is sent in the PUT;
 *   - the form (secrets included) is wiped once the modal has closed, and secrets are never
 *     logged or written to browser storage.
 *
 * Edit sends only the fields that differ from the row as it was when the modal opened
 * (WEB-4649), so a stale list cannot re-enable a setting or overwrite another admin's changes
 * to fields this admin did not touch. Two admins changing the SAME field: last save wins.
 *
 * @remarks Option lists come from the server so a new partner/category/auth type does not
 *          need a Web release. The searchable pickers are selectize widgets (vendor bundle),
 *          rebuilt each time the modal opens or the entity type changes.
 */
class SuperApiIntegrationSettingsController {
    /**
     * @param {Object}   $scope
     * @param {Function} $timeout
     * @param {Object}   SSUserService         - permission/role helper (isSuper, hasPermission)
     * @param {Object}   SSHttpService         - API request helper
     * @param {Object}   SSAlertService        - user-facing alerts
     * @param {Object}   SSConfirmationService - yes/no confirmation modal
     */
    constructor($scope, $timeout, SSUserService, SSHttpService, SSAlertService, SSConfirmationService) {
        const self = this;

        self.$scope = $scope;
        self.$timeout = $timeout;
        self.SSUserService = SSUserService;
        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSConfirmationService = SSConfirmationService;

        self.data = SHOWSUITE.data;

        self.BASE_URL = 'sec/super/api-integration-settings';
        self.SECRET_FIELDS = ['secret_key', 'api_key', 'token'];
        self.FIELD_LABELS = {
            client_id: 'Client ID',
            secret_key: 'Secret Key',
            api_key: 'API Key',
            token: 'Token',
        };

        self.settings = null;   // null = loading
        self.options = null;
        self.filters = {partner: null, category: null, enabled: ''};   // null matches the "All" option of ng-options selects

        self.form = null;           // the row being created/edited (secrets included while open)
        self.editingRow = null;     // the list row being edited, null on create
        self.original = null;       // normalised values of editingRow when the modal opened (edit diff base)
        self.formErrors = {};
        self.replacing = {};        // secret field => true once "Replace" is clicked
        self.showAdvanced = false;
        self.saving = false;

        if (self.canView()) {
            self.init();
        }
    }

    init() {
        const self = this;

        // Wipe the form — and the secrets typed into it — once the modal has fully closed.
        // Done on `hidden` (after the fade) so the dialog does not repaint blank mid-transition.
        jQuery(document).on('hidden.bs.modal', '#apiIntegrationSettingModal', function () {
            self.$scope.$applyAsync(function () {
                self.resetForm();
            });
        });

        self.loadOptions();
        self.loadSettings();
    }

    // ---- Permissions ----

    /** @returns {boolean} May see the page and the list. */
    canView() {
        return this.SSUserService.isSuper() && this.SSUserService.hasPermission('SS_API_INTEGRATION_SETTING_VW');
    }

    /** @returns {boolean} May create, edit and enable/disable. */
    canEdit() {
        return this.SSUserService.hasPermission('SS_API_INTEGRATION_SETTING_EDIT');
    }

    /** @returns {boolean} May delete. */
    canDelete() {
        return this.SSUserService.hasPermission('SS_API_INTEGRATION_SETTING_DELETE');
    }

    // ---- Loading ----

    /** Load the form option lists (partners, categories, auth types, entity lists). */
    loadOptions() {
        const self = this;

        self.SSHttpService.getAPIRequest(self.BASE_URL + '/options').then(function (response) {
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayServerError(response);
                return;
            }
            self.options = response.data;
        });
    }

    /** Load (or reload) the settings list. */
    loadSettings() {
        const self = this;

        loading(true);
        self.SSHttpService.getAPIRequest(self.BASE_URL).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.settings = [];
                self.SSAlertService.parseAndDisplayServerError(response);
                return;
            }
            self.settings = (response.data.api_integration_settings || []).map(function (row) {
                row.is_enabled = parseInt(row.is_enabled || 0, 10);
                row._enabled = row.is_enabled === 1;   // checkbox model
                return row;
            });
        });
    }

    // ---- List helpers ----

    /**
     * Rows matching the partner / category / enabled filters.
     * @returns {Array}
     */
    filteredSettings() {
        const self = this;
        const f = self.filters;

        return (self.settings || []).filter(function (row) {
            if (f.partner && row.partner !== f.partner) return false;
            if (f.category && row.category !== f.category) return false;
            if (f.enabled === '1' && row.is_enabled !== 1) return false;
            if (f.enabled === '0' && row.is_enabled !== 0) return false;
            return true;
        });
    }

    /**
     * Label for a value from one of the option lists; falls back to the raw value.
     * @param {string} listName - partners | categories | auth_types | entity_types
     * @param {string} value
     * @returns {string}
     */
    optionLabel(listName, value) {
        const list = (this.options && this.options[listName]) || [];
        const match = list.find(function (o) { return o.value === value; });
        return match ? match.label : (value || '');
    }

    /** @returns {string} "System-wide" or the developer's name. */
    scopeLabel(row) {
        if (!row.developer_id) return 'System-wide';
        return row.developer_name || 'Developer (removed)';
    }

    /** @returns {string} e.g. "Agency: PropNex", or "--" when no entity is linked. */
    entityLabel(row) {
        if (!row.partner_entity_type) return '--';
        const type = this.optionLabel('entity_types', row.partner_entity_type);
        return type + ': ' + (row.partner_entity_name || '(not found)');
    }

    /** @returns {string} UTC timestamp shown in local time. */
    formatDate(value) {
        if (!value) return '--';
        return moment.utc(value).local().format('DD/MM/YYYY hh:mm A');
    }

    // ---- Enable toggle ----

    /**
     * ng-change of the list's Enabled switch. Disabling asks for confirmation first; the
     * change is saved immediately with a PUT carrying only is_enabled.
     * @param {Object} row
     */
    toggleEnabled(row) {
        const self = this;
        const enable = row._enabled;

        if (!self.canEdit()) {
            row._enabled = !enable;
            return;
        }

        if (enable) {
            self.saveEnabled(row, true);
            return;
        }

        let answered = false;
        self.SSConfirmationService.getConfirmed(
            'Disable integration?',
            'Pushes to ' + self.optionLabel('partners', row.partner) + ' for '
                + self.optionLabel('categories', row.category) + ' will stop immediately.',
            self.SSConfirmationService.TYPE_YES_NO,
            function (option) {
                answered = true;
                if (option !== self.SSConfirmationService.OPTION_YES) {
                    row._enabled = true;
                    return;
                }
                self.saveEnabled(row, false);
            }
        );
        // "No" and the close button dismiss the modal without calling back — revert the switch.
        jQuery('#confirmationModal').one('hidden.bs.modal', function () {
            if (answered) return;
            self.$scope.$applyAsync(function () {
                row._enabled = row.is_enabled === 1;
            });
        });
    }

    /**
     * PUT the enable flag alone; reverts the switch on failure.
     * @param {Object}  row
     * @param {boolean} enable
     */
    saveEnabled(row, enable) {
        const self = this;

        loading(true);
        self.SSHttpService.putAPIRequest(self.BASE_URL + '/' + row.id, {is_enabled: enable ? 1 : 0}).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                row._enabled = row.is_enabled === 1;
                self.SSAlertService.parseAndDisplayServerError(response);
                return;
            }
            row.is_enabled = enable ? 1 : 0;
            row._enabled = enable;
            row.updated_at = response.data.api_integration_setting.updated_at;
            self.SSAlertService.success('Saved', 'Integration ' + (enable ? 'enabled' : 'disabled') + '.');
        });
    }

    // ---- Delete ----

    /**
     * Confirm, then delete. The message recommends disabling instead and spells out the effect.
     * @param {Object} row
     */
    deleteSetting(row) {
        const self = this;

        if (!self.canDelete()) return;

        self.SSConfirmationService.getConfirmed(
            'Delete integration setting?',
            'Deleting removes the endpoint and credentials. Pushes stop until a new setting is created.\n\n'
                + 'To pause pushes temporarily, switch the setting off instead — it keeps the endpoint and credentials.',
            self.SSConfirmationService.TYPE_DANGER,
            function (option) {
                if (option !== self.SSConfirmationService.OPTION_YES) return;

                loading(true);
                self.SSHttpService.deleteAPIRequest(self.BASE_URL + '/' + row.id).then(function (response) {
                    loading(false);
                    if (response instanceof Error) {
                        self.SSAlertService.parseAndDisplayServerError(response);
                        return;
                    }
                    self.SSAlertService.success('Deleted', 'Integration setting deleted.');
                    self.loadSettings();
                });
            }
        );
    }

    // ---- Create / Edit form ----

    /** Open the modal on an empty form. */
    openCreate() {
        const self = this;

        if (!self.canEdit()) return;

        const firstPartner = (self.options && self.options.partners && self.options.partners.length === 1)
            ? self.options.partners[0].value : null;

        // Dropdown models start as null, not '', so the ng-options placeholder option shows.
        self.editingRow = null;
        self.original = null;
        self.form = {
            partner: firstPartner,
            category: null,
            endpoint_url: '',
            auth_type: null,
            client_id: '',
            secret_key: '',
            api_key: '',
            token: '',
            partner_entity_type: null,
            partner_entity_id: null,
            developer_id: null,
            extra_config_text: '',
            is_enabled: true,
        };
        self.openModal();
    }

    /**
     * Open the modal on an existing row. Secrets are never pre-filled.
     * @param {Object} row
     */
    openEdit(row) {
        const self = this;

        if (!self.canEdit()) return;

        self.editingRow = row;
        self.form = {
            partner: row.partner,
            category: row.category,
            endpoint_url: row.endpoint_url,
            auth_type: row.auth_type,
            client_id: row.client_id || '',
            secret_key: '',
            api_key: '',
            token: '',
            partner_entity_type: row.partner_entity_type || null,
            partner_entity_id: row.partner_entity_id || null,
            developer_id: row.developer_id || null,
            extra_config_text: row.extra_config ? JSON.stringify(row.extra_config, null, 2) : '',
            is_enabled: row.is_enabled === 1,
        };
        self.original = self.normalise(self.form, row.extra_config || null);
        self.openModal();
    }

    /** Shared modal opening: reset errors/flags, build the pickers, show. */
    openModal() {
        const self = this;

        self.formErrors = {};
        self.replacing = {};
        self.showAdvanced = !!self.form.extra_config_text;
        self.initPickers();
        jQuery('#apiIntegrationSettingModal').modal('show');
    }

    /** Clear the form and every secret typed into it. */
    resetForm() {
        const self = this;

        if (self.form) {
            self.SECRET_FIELDS.forEach(function (f) { self.form[f] = ''; });
        }
        self.form = null;
        self.editingRow = null;
        self.original = null;
        self.formErrors = {};
        self.replacing = {};
        self.destroyPicker('ais-entity-picker');
        self.destroyPicker('ais-developer-picker');
    }

    /**
     * Credential fields the selected auth type needs (from the options endpoint).
     * @returns {string[]}
     */
    credentialFields() {
        const self = this;
        if (!self.form || !self.options) return [];
        const authType = (self.options.auth_types || []).find(function (a) { return a.value === self.form.auth_type; });
        return authType ? authType.credential_fields : [];
    }

    /** @returns {boolean} Whether the selected auth type uses this credential field. */
    usesField(field) {
        return this.credentialFields().indexOf(field) !== -1;
    }

    /** @returns {boolean} Whether the field is a write-only secret. */
    isSecret(field) {
        return this.SECRET_FIELDS.indexOf(field) !== -1;
    }

    /**
     * Edit mode, the stored row already holds this secret, the auth type is unchanged, and
     * the user has not clicked Replace — show "Secret is set" instead of an input.
     * @param {string} field
     * @returns {boolean}
     */
    secretIsSet(field) {
        const row = this.editingRow;
        return !!(row
            && row.auth_type === this.form.auth_type
            && row['has_' + field]
            && !this.replacing[field]);
    }

    /** Reveal an empty input for a stored secret. */
    startReplace(field) {
        this.replacing[field] = true;
        this.form[field] = '';
    }

    /** Abandon a replacement; the stored secret stays. */
    cancelReplace(field) {
        this.replacing[field] = false;
        this.form[field] = '';
        delete this.formErrors[field];
    }

    /** @returns {boolean} A URL that is filled in but not https. */
    isInsecureUrl() {
        const url = this.form && this.form.endpoint_url;
        return !!url && /^http:\/\//i.test(url.trim());
    }

    /** ng-change of the entity type: the picked entity belongs to the old type, so clear it. */
    entityTypeChanged() {
        const self = this;
        self.form.partner_entity_id = null;
        self.initEntityPicker();
    }

    // ---- Pickers (selectize) ----

    /** Build both searchable pickers for the current form. */
    initPickers() {
        this.initEntityPicker();
        this.initDeveloperPicker();
    }

    /** (Re)build the linked-entity picker for the selected entity type. */
    initEntityPicker() {
        const self = this;
        const type = self.form && self.form.partner_entity_type;
        const list = (type && self.options && self.options.entities && self.options.entities[type]) || [];

        self.buildPicker('ais-entity-picker', list, self.form ? self.form.partner_entity_id : null, function (value) {
            self.form.partner_entity_id = value;
            delete self.formErrors.partner_entity_id;
        });
    }

    /** (Re)build the developer-scope picker. */
    initDeveloperPicker() {
        const self = this;
        const list = (self.options && self.options.entities && self.options.entities.developer) || [];

        self.buildPicker('ais-developer-picker', list, self.form ? self.form.developer_id : null, function (value) {
            self.form.developer_id = value;
            delete self.formErrors.developer_id;
        });
    }

    /**
     * Turn a <select> into a single-value searchable selectize bound to a callback.
     * @param {string}   id        element id
     * @param {Array}    list      [{id, name}]
     * @param {?number}  selected  current id
     * @param {Function} onChange  receives the new id (number) or null
     */
    buildPicker(id, list, selected, onChange) {
        const self = this;

        self.destroyPicker(id);
        self.$timeout(function () {
            const $el = jQuery('#' + id);
            if (!$el.length) return;
            $el.selectize({
                valueField: 'id',
                labelField: 'name',
                searchField: ['name'],
                options: list,
                items: selected ? [selected] : [],
                maxItems: 1,
                create: false,
                allowEmptyOption: true,
                onChange: function (value) {
                    self.$scope.$applyAsync(function () {
                        onChange(value ? parseInt(value, 10) : null);
                    });
                },
            });
        });
    }

    /** Destroy a selectize instance if one exists. */
    destroyPicker(id) {
        const el = jQuery('#' + id)[0];
        if (el && el.selectize) {
            el.selectize.destroy();
        }
    }

    // ---- Validation & save ----

    /**
     * Client-side checks mirroring the server rules. Fills formErrors.
     * @returns {?Object} the parsed extra_config (or null) when valid, false when invalid
     */
    validateForm() {
        const self = this;
        const form = self.form;
        const errors = {};

        if (!form.partner) errors.partner = 'Partner is required.';
        if (!form.category) errors.category = 'Category is required.';
        if (!form.auth_type) errors.auth_type = 'Auth type is required.';

        const url = (form.endpoint_url || '').trim();
        if (!url) {
            errors.endpoint_url = 'Endpoint URL is required.';
        } else if (!/^https?:\/\/[^\s/$.?#][^\s]*$/i.test(url)) {
            errors.endpoint_url = 'Enter a valid URL, e.g. https://partner.example.com/api/push';
        }

        self.credentialFields().forEach(function (field) {
            const value = (form[field] || '').trim();
            if (self.isSecret(field) && self.secretIsSet(field)) return;
            if (!value) errors[field] = self.FIELD_LABELS[field] + ' is required for this auth type.';
        });

        if (form.partner_entity_type && !form.partner_entity_id) {
            errors.partner_entity_id = 'Select the ' + self.optionLabel('entity_types', form.partner_entity_type).toLowerCase() + '.';
        }

        let extraConfig = null;
        const text = (form.extra_config_text || '').trim();
        if (text) {
            try {
                extraConfig = JSON.parse(text);
            } catch (e) {
                extraConfig = undefined;
            }
            if (!extraConfig || typeof extraConfig !== 'object' || Array.isArray(extraConfig)) {
                errors.extra_config = 'Extra config must be a valid JSON object, e.g. {"timeout": 30}.';
                self.showAdvanced = true;
            }
        }

        self.formErrors = errors;
        return Object.keys(errors).length ? false : {extraConfig: extraConfig};
    }

    /**
     * Non-secret form values in a comparable shape: trimmed strings, '' for "not set", ids as
     * strings, is_enabled as 0/1 and extra_config as a key-sorted JSON string (so re-indenting
     * or re-ordering the JSON is not a change).
     * @param {Object}  form
     * @param {?Object} extraConfig - parsed extra_config (null when empty)
     * @returns {Object}
     */
    normalise(form, extraConfig) {
        const self = this;
        const text = function (v) { return v === null || v === undefined ? '' : String(v).trim(); };
        const id = function (v) { return v ? String(parseInt(v, 10)) : ''; };

        return {
            partner: text(form.partner),
            category: text(form.category),
            endpoint_url: text(form.endpoint_url),
            auth_type: text(form.auth_type),
            client_id: text(form.client_id),
            partner_entity_type: text(form.partner_entity_type),
            partner_entity_id: form.partner_entity_type ? id(form.partner_entity_id) : '',
            developer_id: id(form.developer_id),
            extra_config: extraConfig ? self.stableJson(extraConfig) : '',
            is_enabled: form.is_enabled ? 1 : 0,
        };
    }

    /**
     * JSON with object keys sorted at every level (array order is kept).
     * @param {*} value
     * @returns {string}
     */
    stableJson(value) {
        const self = this;
        if (Array.isArray(value)) {
            return '[' + value.map(function (v) { return self.stableJson(v); }).join(',') + ']';
        }
        if (value && typeof value === 'object') {
            return '{' + Object.keys(value).sort().map(function (k) {
                return JSON.stringify(k) + ':' + self.stableJson(value[k]);
            }).join(',') + '}';
        }
        return JSON.stringify(value);
    }

    /**
     * Request body.
     *
     * Create: the whole form. Edit: only the fields that differ from `self.original` (WEB-4649):
     *   - a cleared field is sent as '' (server stores NULL); an omitted one keeps the stored value;
     *   - a changed entity type also sends the entity id (even when empty);
     *   - a changed auth type also sends every credential field the new type uses;
     *   - client_id is only considered when the selected auth type uses it.
     * Secrets, both modes: sent only when typed in (create) or replaced (edit) with a non-empty
     * value, so an untouched secret is absent and the server keeps the stored one.
     *
     * @param {?Object} extraConfig - parsed extra_config, null when empty
     * @returns {Object} an empty object on edit means nothing changed
     */
    buildPayload(extraConfig) {
        const self = this;
        const form = self.form;
        let payload;

        if (!self.editingRow) {
            payload = {
                partner: form.partner,
                category: form.category,
                endpoint_url: form.endpoint_url.trim(),
                auth_type: form.auth_type,
                partner_entity_type: form.partner_entity_type || null,
                partner_entity_id: form.partner_entity_type ? form.partner_entity_id : null,
                developer_id: form.developer_id || null,
                extra_config: extraConfig,
                is_enabled: form.is_enabled ? 1 : 0,
            };
            if (self.usesField('client_id')) {
                payload.client_id = (form.client_id || '').trim();
            }
        } else {
            const now = self.normalise(form, extraConfig);
            const was = self.original;
            const changed = function (field) { return now[field] !== was[field]; };
            const authChanged = changed('auth_type');
            const values = {
                partner: now.partner,
                category: now.category,
                endpoint_url: now.endpoint_url,
                auth_type: now.auth_type,
                client_id: now.client_id,
                partner_entity_type: now.partner_entity_type,
                partner_entity_id: now.partner_entity_id ? parseInt(now.partner_entity_id, 10) : '',
                developer_id: now.developer_id ? parseInt(now.developer_id, 10) : '',
                extra_config: extraConfig || '',
                is_enabled: now.is_enabled,
            };

            payload = {};
            ['partner', 'category', 'endpoint_url', 'auth_type', 'partner_entity_type', 'partner_entity_id',
                'developer_id', 'extra_config', 'is_enabled'].forEach(function (field) {
                if (changed(field)) payload[field] = values[field];
            });
            if ('partner_entity_type' in payload) {
                payload.partner_entity_id = values.partner_entity_id;
            }
            if (self.usesField('client_id') && (authChanged || changed('client_id'))) {
                payload.client_id = values.client_id;
            }
        }

        self.SECRET_FIELDS.forEach(function (field) {
            if (self.usesField(field) && !self.secretIsSet(field) && form[field]) {
                payload[field] = form[field];
            }
        });

        return payload;
    }

    /** Validate, then POST (create) or PUT (edit). */
    save() {
        const self = this;

        if (self.saving || !self.canEdit()) return;

        const valid = self.validateForm();
        if (!valid) return;

        const isEdit = !!self.editingRow;
        const url = isEdit ? self.BASE_URL + '/' + self.editingRow.id : self.BASE_URL;
        const request = isEdit ? 'putAPIRequest' : 'postAPIRequest';
        const payload = self.buildPayload(valid.extraConfig);

        // Edit with nothing changed: no empty PUT (it would only bump updated_at).
        if (isEdit && !Object.keys(payload).length) {
            jQuery('#apiIntegrationSettingModal').modal('hide');
            self.SSAlertService.info('No changes', 'No changes to save.');
            return;
        }

        self.saving = true;
        loading(true);
        self.SSHttpService[request](url, payload).then(function (response) {
            loading(false);
            self.saving = false;

            if (response instanceof Error) {
                self.showServerErrors(response);
                return;
            }

            // Drop the secrets now; the rest of the form is cleared when the modal has closed.
            self.SECRET_FIELDS.forEach(function (f) { self.form[f] = ''; });
            jQuery('#apiIntegrationSettingModal').modal('hide');
            self.SSAlertService.success('Saved', 'Integration setting ' + (isEdit ? 'updated' : 'created') + '.');
            self.loadSettings();
        });
    }

    /**
     * Server errors for fields the form has no inline slot for (shown at the top of the modal).
     * @returns {string[]}
     */
    otherErrors() {
        const self = this;
        const inline = ['partner', 'category', 'endpoint_url', 'auth_type', 'client_id', 'secret_key', 'api_key',
            'token', 'partner_entity_type', 'partner_entity_id', 'developer_id', 'extra_config'];
        return Object.keys(self.formErrors || {}).filter(function (k) {
            return inline.indexOf(k) === -1;
        }).map(function (k) { return self.formErrors[k]; });
    }

    /**
     * Map a 412 field-keyed error payload onto the form; anything else goes to the alert bar.
     * @param {Error} response
     */
    showServerErrors(response) {
        const self = this;
        const fieldErrors = response && response.data;

        if (response && response.code == 412 && fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)) {
            const errors = {};
            Object.keys(fieldErrors).forEach(function (field) {
                const messages = fieldErrors[field];
                errors[field] = Array.isArray(messages) ? messages[0] : messages;
            });
            if (errors.extra_config) self.showAdvanced = true;
            self.formErrors = errors;
            if (Object.keys(errors).length) return;
        }

        self.SSAlertService.parseAndDisplayServerError(response);
    }
}

SuperApiIntegrationSettingsController.$inject = ['$scope', '$timeout', 'SSUserService', 'SSHttpService', 'SSAlertService', 'SSConfirmationService'];
app.controller('SuperApiIntegrationSettingsController', SuperApiIntegrationSettingsController);
